import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StoreLinkSettings } from '@reform-lite/contracts';
import { apiClient } from '@/lib/api-client';

/** One owner's export queue for a store (RL-452). */
export type ExportQueueState = {
  pending: number;
  failed: number;
  /** Back Office only: uploads the export has not picked up yet. */
  undiscovered: number;
  /** Link only: when Link last reported; null = unknown. */
  reportedAt: string | null;
};

/** A store's connector ownership as `GET /connectors/locations/settings` lists it. */
export type ConnectorLocationSettings = StoreLinkSettings & {
  locationId: string;
  location: { id: string; code: string; name: string };
  configured: boolean;
  backofficeQueue: ExportQueueState;
  linkQueue: ExportQueueState;
  updatedAt: string | null;
  updatedBy: string | null;
};

const KEY = ['connector-location-settings'] as const;

export function useConnectorLocationSettings(enabled: boolean) {
  return useQuery({
    queryKey: KEY,
    enabled,
    // The queues are what an owner switch waits on, so they are kept fresh
    // while the operator is looking at them.
    refetchInterval: 10_000,
    queryFn: async (): Promise<ConnectorLocationSettings[]> => {
      const response = await apiClient.get<ConnectorLocationSettings[]>(
        '/connectors/locations/settings',
      );
      return response.data;
    },
  });
}

export function useUpdateConnectorLocationSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { locationId: string; settings: StoreLinkSettings }) => {
      await apiClient.put(`/connectors/locations/${input.locationId}/settings`, input.settings);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
